import { useMemo } from 'react';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useMultiLEMemberAccess } from '../hooks/useMultiLEMemberAccess';
import { useRoles } from '../hooks/useRoles';
import { useSingleLEMemberAccess } from '../hooks/useSingleLEMemberAccess';
import type { MemberRole, MemberRoleMap, MemberRolesForEntity } from '../utils/constants';
import type { PendingInviteWithGracePeriod } from '../utils/gracePeriod';
import { getPendingInviteEffectiveAt } from '../utils/gracePeriod';

import { MultiLEProductAccessGroup } from './InviteTray/MultiLEProductAccessGroup';
import { ProductAccessGroup } from './InviteTray/ProductAccessGroup';
import type { PendingRoleInfoEntry } from './PendingRoleInfo';
import { PendingRoleInfo } from './PendingRoleInfo';

type PendingProtectedRole = {
  role: MemberRoleMap;
  effectiveAt: Date;
};

/** Pending protected roles by product key, for the invites to one legal entity. */
type PendingRolesByProduct = Map<string, PendingProtectedRole>;

function isNotEditable() {
  return false;
}

/**
 * The invited roles per legal entity, merged across invites. Invites without a
 * legal entity are skipped.
 */
function getInvitedLegalEntityRoles(
  invites: PendingInviteWithGracePeriod[],
): MemberRolesForEntity[] {
  const rolesByEntity = new Map<string, Set<MemberRole>>();
  for (const invite of invites) {
    if (invite.legalEntityId) {
      const roles = rolesByEntity.get(invite.legalEntityId) ?? new Set<MemberRole>();
      for (const role of invite.roles ?? []) roles.add(role);
      rolesByEntity.set(invite.legalEntityId, roles);
    }
  }
  return [...rolesByEntity].map(([legalEntityId, roles]) => ({
    legalEntityId,
    roles: [...roles],
  }));
}

/**
 * For each legal entity, the protected roles still in their grace period, keyed by
 * product. A product with several pending roles keeps the one that starts first.
 */
function getPendingRolesByEntity(
  invites: PendingInviteWithGracePeriod[],
  roleMap: Record<MemberRole, MemberRoleMap>,
  now: Date,
): Map<string, PendingRolesByProduct> {
  const result = new Map<string, PendingRolesByProduct>();
  for (const invite of invites) {
    const { legalEntityId } = invite;
    if (legalEntityId) {
      const byProduct = result.get(legalEntityId) ?? new Map<string, PendingProtectedRole>();
      for (const roleId of invite.roles ?? []) {
        const role = roleMap[roleId];
        const protection = role?.protection ?? null;
        const effectiveAt = protection
          ? getPendingInviteEffectiveAt(invite, protection, now, roleId)
          : undefined;
        const current = role ? byProduct.get(role.productKey) : undefined;
        if (role && protection && effectiveAt && (!current || effectiveAt < current.effectiveAt)) {
          byProduct.set(role.productKey, {
            role,
            effectiveAt,
          });
        }
      }
      result.set(legalEntityId, byProduct);
    }
  }
  return result;
}

type PendingInviteRoleInfoProps = {
  pendingRole: PendingProtectedRole;
  /** Fixed clock from the caller; omit for a live clock. */
  now?: Date;
};

function PendingInviteRoleInfo({ pendingRole, now }: PendingInviteRoleInfoProps) {
  const { role, effectiveAt } = pendingRole;
  const entries = useMemo<PendingRoleInfoEntry[]>(() => [{ effectiveAt }], [effectiveAt]);

  return (
    <PendingRoleInfo variant="pendingInvite" roleName={role.name} entries={entries} now={now} />
  );
}

function getRoleInfo(
  pendingRoles: PendingRolesByProduct | undefined,
  productKey: string,
  now: Date | undefined,
) {
  const pendingRole = pendingRoles?.get(productKey);
  return pendingRole ? <PendingInviteRoleInfo pendingRole={pendingRole} now={now} /> : undefined;
}

type InvitedRolesProps = {
  invitedLegalEntityRoles: MemberRolesForEntity[];
  pendingRolesByEntity: Map<string, PendingRolesByProduct>;
  now?: Date;
};

function SingleLEInvitedRoles({
  invitedLegalEntityRoles,
  pendingRolesByEntity,
  now,
}: InvitedRolesProps) {
  const invitedRoles = useMemo(
    () => invitedLegalEntityRoles.flatMap((ler) => ler.roles ?? []),
    [invitedLegalEntityRoles],
  );
  const { rows } = useSingleLEMemberAccess(invitedRoles);

  // A single-entity org has one entity's pending roles at most.
  const pendingRoles = useMemo(() => [...pendingRolesByEntity.values()][0], [pendingRolesByEntity]);

  const rowsWithRoleInfo = useMemo(
    () => rows.map((row) => ({ ...row, roleInfo: getRoleInfo(pendingRoles, row.productKey, now) })),
    [now, pendingRoles, rows],
  );

  return <ProductAccessGroup rows={rowsWithRoleInfo} isEditable={false} />;
}

function MultiLEInvitedRoles({
  invitedLegalEntityRoles,
  pendingRolesByEntity,
  now,
}: InvitedRolesProps) {
  const { entities } = useMultiLEMemberAccess({
    memberRolesForEntity: invitedLegalEntityRoles,
    isEntityEditable: isNotEditable,
  });

  const invitedEntityIds = useMemo(
    () => new Set(invitedLegalEntityRoles.map((ler) => ler.legalEntityId)),
    [invitedLegalEntityRoles],
  );

  const entitiesWithRoleInfo = useMemo(
    () =>
      entities.map((entity) => {
        const { entityId } = entity.entityRow;
        const pendingRoles = pendingRolesByEntity.get(entityId);
        return {
          ...entity,
          defaultExpanded: invitedEntityIds.has(entityId),
          productRows: entity.productRows.map((row) => ({
            ...row,
            roleInfo: getRoleInfo(pendingRoles, row.productKey, now),
          })),
        };
      }),
    [entities, invitedEntityIds, now, pendingRolesByEntity],
  );

  return <MultiLEProductAccessGroup entities={entitiesWithRoleInfo} isEditable={false} />;
}

export type PendingInviteRolesProps = {
  /** The member's pending invites, typically one per legal entity. */
  invites: PendingInviteWithGracePeriod[];
  /** Current time. Defaults to now; pass a fixed value in tests and stories. */
  now?: Date;
};

/**
 * Read-only access of a pending invite, rendered with the edit member tray's access
 * group (`useSingleLEMemberAccess` / `useMultiLEMemberAccess`) so both look the same.
 * Protected roles still in their grace period get a `PendingRoleInfo` icon.
 */
export function PendingInviteRoles({ invites, now }: PendingInviteRolesProps) {
  const { roleMap } = useRoles();
  const { selectedOrganization } = useOrganization();

  const invitedLegalEntityRoles = useMemo(() => getInvitedLegalEntityRoles(invites), [invites]);

  // `useMultiLEMemberAccess` seeds its selection once, so remount the access group
  // whenever the invited roles change to keep the read-only view current.
  const invitedRolesKey = useMemo(
    () =>
      invitedLegalEntityRoles
        .map(
          ({ legalEntityId, roles }) => `${legalEntityId}:${[...(roles ?? [])].sort().join(',')}`,
        )
        .sort()
        .join('|'),
    [invitedLegalEntityRoles],
  );

  // Derived on every render so the grace period check stays current when `now`
  // is omitted. PendingRoleInfo re-renders itself while the role is pending.
  const pendingRolesByEntity = getPendingRolesByEntity(invites, roleMap, now ?? new Date());

  const isMultiEntity = (selectedOrganization?.legalEntities?.length ?? 0) > 1;
  const InvitedRoles = isMultiEntity ? MultiLEInvitedRoles : SingleLEInvitedRoles;

  return (
    <InvitedRoles
      key={invitedRolesKey}
      invitedLegalEntityRoles={invitedLegalEntityRoles}
      pendingRolesByEntity={pendingRolesByEntity}
      now={now}
    />
  );
}
